import {Trans} from "@lingui/react/macro";
import {useNavigate, useParams} from "react-router";
import * as v from "valibot";
import createId, {IdType} from "../../models/create-id";
import type {
  CategoryEntry,
  CategoryGroupEntryId,
  CategoryGroupId,
  CategoryId,
  GroupRepo,
} from "../../models/GroupRepo";
import {Col, css} from "../../ui/Box";
import Button from "../../ui/Button";
import {isEmoji} from "../../utils";
import {createSorter} from "../../utils/sort-utils";
import {TextField} from "../form/fields";
import {useForm} from "../form/Form";

const categoryFormSchema = v.object({
  categoryName: v.pipe(v.string(), v.nonEmpty(), v.maxLength(256)),
  emoji: v.pipe(v.string(), v.nonEmpty(), v.check(isEmoji, "no_emoji")),
});

export const CreateDialog = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const navigate = useNavigate();
  const {Form, field} = useForm({
    initial: {categoryName: "", emoji: "🛒"},
    schema: categoryFormSchema,
    onSubmit: ({categoryName, emoji}) => {
      const catId = createId(IdType.Category);
      groupRepo.store.transaction(() => {
        groupRepo.Categories.set(catId, {
          id: catId,
          createdAt: new Date(),
          emoji,
          name: categoryName,
        });
        for (const catGroupId of Object.keys(
          groupRepo.CategoryGroups.getAll()
        ) as CategoryGroupId[]) {
          const catEntryIds = groupRepo.indexes.getSliceRowIds(
            "sortedCategoryEntries",
            catGroupId
          ) as CategoryGroupEntryId[];
          const getLastSortIndex = () => {
            if (catEntryIds.length === 0) return null;
            const lastEntry = groupRepo.CategoryGroupEntries.getById(
              catEntryIds[catEntryIds.length - 1]
            )!;
            return lastEntry.sortIndex;
          };
          const sortIndex = createSorter().addAtEnd({
            currLastValue: getLastSortIndex(),
            expectedRemainingCount: 20,
          });
          const catEntryId = createId(IdType.CategoryGroupEntry);
          groupRepo.CategoryGroupEntries.set(catEntryId, {
            id: catEntryId,
            categoryId: catId,
            categoryGroupId: catGroupId,
            sortIndex,
          });
        }
      });
      navigate("..");
    },
  });

  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <h1>Add Category</h1>
      <Form className={css({display: "flex", flexDir: "column", sp: "16"})}>
        <TextField
          field={field("categoryName")}
          label={<Trans>Name</Trans>}
          data-drawer-autofocus
        />
        <TextField field={field("emoji")} label={<Trans>Emoji</Trans>} />
      </Form>
    </Col>
  );
};

type InnerEditProps = {
  cat: CategoryEntry;
  groupRepo: GroupRepo;
};

const InnerEdit = ({groupRepo, cat}: InnerEditProps) => {
  const navigate = useNavigate();
  const handleDelete = () => {
    const items = Object.values(groupRepo.Items.getAll());
    const affectedItems = items.filter((i) => i.categoryId === cat.id);
    groupRepo.store.transaction(() => {
      affectedItems.forEach((i) => {
        groupRepo.Items.patch(i.id, {categoryId: null});
      });
      for (const entry of Object.values(groupRepo.CategoryGroupEntries.getAll())) {
        if (entry.categoryId === cat.id) {
          groupRepo.CategoryGroupEntries.delete(entry.id);
        }
      }
      groupRepo.Categories.delete(cat.id);
    });
    navigate("..");
  };

  const {Form, field} = useForm({
    initial: {categoryName: cat.name, emoji: cat.emoji ?? ""},
    schema: categoryFormSchema,
    onSubmit: ({categoryName, emoji}) => {
      groupRepo.Categories.patch(cat.id, {
        name: categoryName,
        emoji: emoji || null,
      });
      navigate("..");
    },
  });
  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <h1>Edit {cat.name} Category</h1>
      <div>
        <Button onClick={handleDelete}>del</Button>
      </div>
      <Form>
        <TextField label="Name" field={field("categoryName")} data-drawer-autofocus />
        <TextField label="Emoji" field={field("emoji")} />
      </Form>
    </Col>
  );
};

export const EditDialog = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const params = useParams<{itemId: CategoryId}>();
  const cat = groupRepo.Categories.useById(params.itemId!);
  return cat ? <InnerEdit cat={cat} groupRepo={groupRepo} /> : <div>404</div>;
};
